import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeRu from '@angular/common/locales/ru';
import localeUz from '@angular/common/locales/uz';
import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  NavigationEnd,
  type NavigationError,
  provideRouter,
  Router,
  TitleStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
  withNavigationErrorHandler,
  withViewTransitions,
} from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { filter, take } from 'rxjs';
import { routes } from './app.routes';
import { STORAGE_KEYS } from './core/constants';
import { apiHeadersInterceptor } from './core/http/api-headers.interceptor';
import { refreshTokenInterceptor } from './core/http/refresh-token.interceptor';
import { LangStore } from './core/lang.service';
import { MapConfigStore } from './core/map-config-store';
import { ThemeStore } from './core/theme.service';
import { AppTitleStrategy } from './core/title.strategy';
import { provideApi } from './data/api.providers';

registerLocaleData(localeRu);
registerLocaleData(localeUz);

// Chrome/Firefox; Safari при 404 и при index.html вместо чанка (try_files отдаёт его как text/html).
const CHUNK_LOAD_ERROR =
  /dynamically imported module|Importing a module script failed|is not a valid JavaScript MIME type/i;

/** Вкладка открыта до деплоя: чанков её сборки на сервере уже нет — один раз берём свежий index.html. */
function reloadOnStaleChunk({ error, url }: NavigationError): void {
  // Офлайн перезагрузка бесполезна: вкладка уйдёт на страницу ошибки браузера и потеряет ввод.
  if (!(error instanceof Error) || !CHUNK_LOAD_ERROR.test(error.message) || !navigator.onLine) {
    return;
  }
  try {
    // Флаг уже стоит или его не поставить — не перезагружаем: битый деплой зациклил бы перезагрузки.
    if (
      typeof sessionStorage === 'undefined' ||
      sessionStorage.getItem(STORAGE_KEYS.CHUNK_RELOAD)
    ) {
      return;
    }
    sessionStorage.setItem(STORAGE_KEYS.CHUNK_RELOAD, '1');
  } catch {
    return;
  }
  location.assign(url);
}

function clearChunkReloadFlag(): void {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEYS.CHUNK_RELOAD);
    }
  } catch {
    // Хранилище недоступно — флага в нём и нет.
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withViewTransitions(),
      withComponentInputBinding(),
      // Дефолт роутера — 'disabled': один скроллер на все роуты протекал бы оффсетом между страницами.
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
      withNavigationErrorHandler(reloadOnStaleChunk),
    ),
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    // Порядок интерцепторов важен: заголовки ставятся до отправки, refresh повторяет уже готовый запрос.
    provideHttpClient(withInterceptors([apiHeadersInterceptor, refreshTokenInterceptor])),
    provideApi(),
    // fallbackLang: при дырке в uz.json покажется русская строка, а не голый ключ.
    provideTranslateService({
      // failOnError: иначе v18 глушит сбой в {} — use() «успешен», а пустой словарь кэшируется навсегда.
      loader: provideTranslateHttpLoader({ prefix: '/i18n/', suffix: '.json', failOnError: true }),
      fallbackLang: 'ru',
    }),
    provideAppInitializer(() => inject(ThemeStore).initTheme()),
    provideAppInitializer(() => inject(LangStore).init()),
    // Ничего не возвращаем: старт ждать сеть не должен, ключи доедут до открытия шита с картой.
    provideAppInitializer(() => {
      inject(MapConfigStore).load();
    }),
    // Флаг перезагрузки снимает удачная навигация, а не старт: иначе битый деплой зациклил бы перезагрузки.
    provideAppInitializer(() => {
      inject(Router)
        .events.pipe(
          filter((event) => event instanceof NavigationEnd),
          take(1),
        )
        .subscribe(clearChunkReloadFlag);
      // bfcache вернул старый документ: прерванная перезагрузка не должна запрещать следующую.
      if (typeof window !== 'undefined') {
        window.addEventListener('pageshow', (event) => {
          if (event.persisted) {
            clearChunkReloadFlag();
          }
        });
      }
    }),
    { provide: LOCALE_ID, useValue: 'ru-RU' },
  ],
};
